/**
 * voice-v8 — was wie ein Schlüssel aussieht, wird zu [GEHEIM]. (28.09.2026, Ticket 2770)
 *
 * ## Der Anlass
 *
 * Beim Einrichten eines Dienstes über das Cloudflare-Dashboard entstand ein
 * API-Token. Vespera las ihn von der Seite (browser_get_page_text), fand
 * keinen sicheren Ort und schrieb ihn per ticket_update in die Notizen eines
 * Tickets. In zwei gespeicherten Werkzeugkarten des Chats stand er ebenfalls.
 * Martins Regel: *„Gib mir die Schlüssel NICHT hier im Verlauf und nicht in
 * einem Ticket“* — *„nur im Kopf, nie als Query-Parameter, nie in einem Log“*.
 *
 * ## Was hier steht
 *
 * EINE Regel, wie ein Schlüssel aussieht. Angewandt wird sie dort, wo
 * Werkzeug-Ein- und -Ausgaben den Server verlassen oder liegen bleiben — je
 * an der einen Stelle, die das jeweils besitzt:
 *
 *   server/sse-send.ts         die Karte am Gerät, für jeden Zug-Weg
 *   memory/store-sqlite.ts     die gespeicherte Karte (appendMessage — der
 *                              einzige Schreiber von chat_messages, auch
 *                              für den Anruf-Prozess)
 *   server/message-bus.ts      die Karte auf dem zweiten Gerät
 *   agent/index.ts             die Karte im Anruf, live
 *   memory/tickets.ts          jedes Textfeld eines Tickets
 *   server/handlungs-log.ts    Bezug und Ergebnis im Handlungsprotokoll
 *   server/heavy-task.ts       die Schritte eines Hintergrund-Auftrags
 *                              (Task-Manager-Liste, Nachbesserung 2770)
 *   server/werkzeug-ergebnis.ts  der Grund eines Fehlschlags, bevor er in
 *                              `werkzeug_lauf.grund` geht (kuerzeGrund)
 *
 * Das Journal trägt keine Werkzeug-Ein- oder -Ausgaben (nachgesehen am
 * 28.09.2026: `[werkzeug-zeit]` nennt Name und Grund-Code, `[mcp-tool-run]`
 * Name und Modell). Das Modell selbst bekommt die Ergebnisse UNGESCHWÄRZT:
 * nur so kann es einen frisch erzeugten Schlüssel mit `schluessel_ablegen`
 * wegräumen (tools/schluessel.ts).
 *
 * ## Drei Erkennungen
 *
 *   1. Bekannte Anfänge (sk-, sk-ant-, cfat_, ghp_ und Verwandte,
 *      github_pat_, panel_, xoxb- und Verwandte, AKIA/ASIA, AIza, glpat-)
 *      und private PEM-Schlüssel — überall, auch in Adressen: ein Schlüssel
 *      als Query-Parameter ist genau der Fall, den Martin verbietet.
 *   2. Hohe Entropie ab 32 Zeichen — NUR außerhalb von Adressen, sonst
 *      verlöre jeder Drive- oder Sheets-Link seinen Pfad.
 *   3. In Adressen: ein Parameter, dessen Name ein Geheimnis ankündigt
 *      (`api_key=`, `token=`, `X-Amz-Signature=`) — schwaerzeAdresse.
 *
 * ## Die Schwelle der Entropie ist gemessen, nicht geschätzt
 *
 * Shannon-Entropie in Bit je Zeichen, gemessen am 28.09.2026 (je 2000 Zufalls-
 * folgen aus A–Z, a–z, 0–9 und einige echte Bezeichner aus diesem Haus):
 *
 *     Zufall, 32 Zeichen    Minimum 4,02   Median 4,56
 *     Zufall, 40 Zeichen    Minimum 4,26   Median 4,75
 *     Zufall, 64 Zeichen    Minimum 4,75   Median 5,16
 *     Bezeichner camelCase  3,6–3,7   (werkzeugZeitZeile…2702)
 *     Dateiname mit Datum   4,11      (40 Zeichen)
 *     deutsch mit Bindestr. 4,25      (52 Zeichen)
 *     UUID / SHA-256        3,39 / 3,75
 *
 * Die Schwelle wächst mit der Länge, weil auch die höchstmögliche Entropie
 * mit ihr wächst: 0,8 · log2(min(Länge, 64)). Bei 32 Zeichen 4,0, bei 40
 * Zeichen 4,26, bei 52 Zeichen 4,56.
 *
 * ⚠️ Bewusst NICHT erkannt:
 *    - reine Hex-Folgen (Prüfsummen, Commit-Kennungen, UUIDs). Das kostet
 *      Hex-Schlüssel ohne bekannten Anfang — dafür bleibt jede Prüfsumme im
 *      Verlauf lesbar. Den Schutz trägt dort die Prompt-Regel.
 *    - Folgen ohne Ziffer (lange Wörter, camelCase ohne Nummer).
 *    - Folgen über 512 Zeichen (Bilder als Base64).
 * ⚠️ Mit erkannt: Kennungen, die wie Zufall aussehen — eine Drive-Datei-ID
 *    oder eine Sitzungskennung außerhalb eines Links. In einer gespeicherten
 *    Karte steht dann [GEHEIM]; die Karte zeigt Links, keine rohen IDs, und
 *    das Modell bekommt sie ohnehin ungeschwärzt.
 */

/** Was an der Stelle eines Schlüssels steht. */
export const GEHEIM = "[GEHEIM]";

/**
 * Werkzeug-Eingaben, die IMMER geheim sind — gleich, wie der Wert aussieht.
 * Ein Passwort wie „Sommer2026!“ erkennt keine Regel; es steht hier, weil
 * das Werkzeug es ablegt (tools/schluessel.ts).
 */
export const GEHEIME_EINGABEN: Readonly<Record<string, readonly string[]>> = {
  schluessel_ablegen: ["wert"],
};

/** Zeichen, aus denen Schlüssel mit bekanntem Anfang bestehen. */
const Z = "A-Za-z0-9_\\-";

/**
 * Zeichen einer Folge ohne bekannten Anfang: dazu `+` und `/` — das
 * Base64-Alphabet. (29.09.2026, Nachbesserung 2770)
 *
 * ⚠️ Bis dahin fehlte `/`, weil er Pfade trennt. Standard-Base64 zerfiel
 *    daran in Stücke unter 32 Zeichen und blieb stehen — gemessen an je 2000
 *    Zufallsschlüsseln: 653 in der Form eines AWS-Secrets (40 Zeichen), 586
 *    in der Form des eigenen Hauptschlüssels (32 Bytes, 44 Zeichen). Ob eine
 *    Folge mit `/` ein Pfad oder ein Schlüssel ist, entscheidet jetzt
 *    `ersetzeFolge` — nicht mehr das Zeichen.
 */
const B = `${Z}+/`;

/*
 * Wo ein Schlüssel ANFANGEN darf: davor steht kein Zeichen der Folge und kein
 * Backslash — „task-…“ oder „desk-…“ enthalten `sk-`, sind aber keiner.
 *
 * ⚠️ ODER direkt hinter einem Zeichen in JSON-Schreibweise (`\n`, `\t`,
 *    `\\`, …). Viele Werkzeuge liefern ihr Ergebnis als JSON-Text; darin
 *    steht vor dem Token der Buchstabe `n` von `\n`. Ohne diese Ausnahme
 *    fiele ein Token nach einem Zeilenumbruch durch — oder das `n` würde
 *    mitgeschwärzt, und aus `\n` würde `\[`: kaputtes JSON.
 */
const anfang = (klasse: string): string => `(?:(?<![${klasse}\\\\])|(?<=\\\\[nrtbf\\\\]))`;

/** Bekannte Anfänge. `AIza`: Google-API-Schlüssel (reisten bis Ticket 2811 als `?key=` in Adressen, stehen aber weiter in alten Protokollen). */
const PRAEFIX_RE = new RegExp(
  `${anfang(`${Z}+`)}(?:sk-|cfat_|gh[pousr]_|github_pat_|panel_|xox[abposr]-|xapp-|glpat-|A[KS]IA|AIza)([${Z}]{16,})`,
  "g",
);

/** Private Schlüssel im PEM-Format, samt Kopf und Fuß. */
const PEM_RE = /-----BEGIN [A-Z ]*PRIVATE KEY-----[\s\S]*?-----END [A-Z ]*PRIVATE KEY-----/g;

/*
 * Adressen ODER eine Zeichenfolge ab 32 Zeichen. Die Adresse steht vorn in
 * der Alternative: beginnt an einer Stelle eine Adresse, gehört alles bis
 * zum nächsten Leerraum oder Anführungszeichen zu ihr — ihr Pfad bleibt
 * stehen, ihre Parameter prüft `schwaerzeAdresse`.
 */
const ADRESSE_ODER_FOLGE_RE = new RegExp(
  `((?:https?|wss?|ftp):\\/\\/[^\\s"'<>\\\\]+|data:[^\\s"'<>\\\\]+)|${anfang(B)}([${B}]{32,512}={0,2})(?![${B}])`,
  "gi",
);

/** Parameter einer Adresse: `?name=wert`, `&name=wert`, `#name=wert`. */
const PARAMETER_RE = /([?&#])([^=&#]*)=([^&#]+)/g;

/**
 * Ein Parametername, der ein Geheimnis ankündigt — am Ende des Namens, klein
 * gelesen: `key`, `api_key`, `apiKey`, `access_token`, `client_secret`,
 * `password`, `X-Amz-Signature`, `X-Amz-Credential`. Nicht: `keyword`,
 * `author`, `design`.
 */
const GEHEIMER_NAME = /(?:key|token|secret|passw(?:or)?d|pass|pwd|auth|sig|signature|credentials?)$/;

/**
 * Ein Platzhalter aus Quelltext oder Anleitung — `${apiKey}`, `{API_KEY}`,
 * `$GEMINI_KEY` — ist kein Wert. Gemessen: 20 Zeilen im Quelltext dieses
 * Hauses bauen so die Gemini-Adresse; ein Ticket, das eine davon zitiert,
 * behält sie lesbar.
 */
const PLATZHALTER = /^\$?\{|^\$[A-Za-z_]/;

/** Sieht der Körper hinter einem bekannten Anfang nach Zufall aus? */
function koerperZufaellig(koerper: string): boolean {
  // „panel_verbindung_fuer_kunden“ ist ein Werkzeugname, kein Schlüssel.
  return /[0-9]/.test(koerper) || (/[A-Z]/.test(koerper) && /[a-z]/.test(koerper));
}

/** Shannon-Entropie in Bit je Zeichen. */
export function entropie(s: string): number {
  if (!s) return 0;
  const zaehler = new Map<string, number>();
  for (const c of s) zaehler.set(c, (zaehler.get(c) ?? 0) + 1);
  let h = 0;
  for (const n of zaehler.values()) {
    const p = n / s.length;
    h -= p * Math.log2(p);
  }
  return h;
}

/** Liegt die Entropie über der gemessenen Schwelle für diese Länge? */
function zufaellig(kern: string): boolean {
  return entropie(kern) >= 0.8 * Math.log2(Math.min(kern.length, 64));
}

/** Ist diese Zeichenfolge (ab 32 Zeichen, ohne `/`) ein Schlüssel? */
function folgeIstGeheim(folge: string): boolean {
  const kern = folge.replace(/=+$/, "");
  if (!/[0-9]/.test(kern) || !/[A-Za-z]/.test(kern)) return false;
  if (/^[0-9a-fA-F-]+$/.test(kern)) return false; // Hex, Prüfsumme, UUID
  return zufaellig(kern);
}

/**
 * Ein Glied zwischen zwei `/`, das ein Wort ist: ganz klein, ganz groß oder
 * mit großem Anfang, ab vier Buchstaben — `share`, `HTML`, `Users`.
 */
const WORT_GLIED = /(?:^|\/)(?:[a-z]{4,}|[A-Z]{4,}|[A-Z][a-z]{3,})(?=\/|$)/;

/** Ein Pfad: mindestens zwei `/` und ein Glied, das ein Wort ist. */
function istPfad(kern: string): boolean {
  return (kern.match(/\//g) ?? []).length >= 2 && WORT_GLIED.test(kern);
}

/*
 * Eine Folge mit `/`: Base64 im Ganzen — oder ein Pfad, dessen Glieder
 * einzeln zählen wie jede Folge ohne `/`. (29.09.2026, Nachbesserung 2770)
 *
 * Base64 im Ganzen heißt, alles zugleich:
 *   - kein `-` und kein `_`: die gehören zum Alphabet für Adressen, das
 *     statt `+` und `/` genau diese beiden nimmt — ein Schlüssel mischt die
 *     Alphabete nicht, ein Pfad hat sie fast immer;
 *   - kein Pfad: nicht zwei `/` und dazu ein Glied, das ein Wort ist;
 *   - groß, klein und Ziffer;
 *   - Entropie über der Schwelle.
 *
 * Gemessen am 29.09.2026, je 2000 Zufallsschlüssel, stehen geblieben:
 *
 *                              bisher   jetzt
 *     Base64, 40 Zeichen         653    6–7    (Form eines AWS-Secrets)
 *     Base64, 32 Bytes (44)      586    8–10   (Form des Hauptschlüssels)
 *     Base64, 64 Bytes (88)       59    0–1
 *
 * Der Rest sind Schlüssel ohne Ziffer, ohne `/` unter der Schwelle, oder mit
 * zwei `/` und einem Wort dazwischen. Mit Wörtern ab DREI Buchstaben wären
 * es 14–19 bei 40 Zeichen gewesen — ohne einen einzigen Pfad mehr zu retten.
 *
 * Fehlalarme: keiner neu unter 164.333 Pfaden dieser Maschine (/usr,
 * node_modules, dieses Haus) und keiner neu unter 299.297 Zeilen Quelltext
 * (ohne Tests). Ohne die Pfad-Regel waren es 600 Pfade
 * (`/usr/share/perl5/HTML/FormatMarkdown.pm`), ohne die Regel für `-`/`_`
 * zwölf Zeilen Quelltext (`/Users/…/voice-v8/PLAN.md` (heute docs/archiv/PLAN.md)).
 */
function ersetzeFolge(folge: string): string {
  if (!folge.includes("/")) return folgeIstGeheim(folge) ? GEHEIM : folge;
  const kern = folge.replace(/=+$/, "");
  const base64 =
    !/[-_]/.test(kern) &&
    !istPfad(kern) &&
    /[A-Z]/.test(kern) &&
    /[a-z]/.test(kern) &&
    /[0-9]/.test(kern) &&
    zufaellig(kern);
  if (base64) return GEHEIM;
  return folge.replace(/[^/]{32,}/g, (glied) => (folgeIstGeheim(glied) ? GEHEIM : glied));
}

/*
 * Eine Adresse: der Pfad bleibt, ihre Parameter werden geprüft.
 * (29.09.2026, Nachbesserung 2770)
 *
 * Martin: *„nie als Query-Parameter“*. Bis dahin prüfte in einer Adresse nur
 * die Regel der bekannten Anfänge — `?api_key=<40 Zufallszeichen>` blieb in
 * Karte, Ticket und Protokoll stehen. Jetzt verliert ein Parameter seinen
 * Wert, wenn
 *   - sein NAME ein Geheimnis ankündigt (GEHEIMER_NAME) und der Wert
 *     mindestens 8 Zeichen hat und kein Platzhalter ist — gleich, wie der
 *     Wert sonst aussieht; oder
 *   - der Wert, entschlüsselt (`%3D` → `=`), einen bekannten Anfang, einen
 *     PEM-Schlüssel oder selbst eine Adresse mit geheimem Parameter trägt
 *     (`redirect_uri=…%3Ftoken%3D…`) — roh fiele der Anfang hinter `%3D`
 *     durch, denn vor ihm steht dann ein `D`.
 *
 * ⚠️ NICHT die Entropie. Gemessen am 29.09.2026 an den gespeicherten Karten:
 *    von 864 Adressen mit Parametern hätte sie 101 verändert, darunter
 *    23 Kalender-Links (`eid`, die Kennung des Termins) und 49 Bilder von
 *    Getty und iStock (`c`, die Signatur, ohne die das Bild nicht lädt).
 *    Ein Link ohne sie führt nirgends hin. Der Name dagegen traf dort
 *    sechs: `resourcekey` (Zugang zu geteilten Drive-Dateien) zweimal,
 *    `token` zweimal, `api_key` einmal und einmal eine Adresse, die eine
 *    solche als Parameter trug.
 * ⚠️ Der Pfad bleibt ausgenommen — dort stehen Drive- und Sheets-Kennungen.
 */
function schwaerzeAdresse(adresse: string): string {
  if (adresse.startsWith("data:")) return adresse;
  const i = adresse.search(/[?#]/);
  if (i < 0) return adresse;
  return (
    adresse.slice(0, i) +
    adresse.slice(i).replace(PARAMETER_RE, (treffer: string, trenner: string, name: string, wert: string) => {
      const klar = entschluesselt(wert);
      const geheim =
        (GEHEIMER_NAME.test(entschluesselt(name).toLowerCase()) && klar.length >= 8 && !PLATZHALTER.test(klar)) ||
        schwaerzeMit(klar, false) !== klar;
      return geheim ? `${trenner}${name}=${GEHEIM}` : treffer;
    })
  );
}

/** `%2F` → `/`; eine kaputte %-Folge bleibt roh. */
function entschluesselt(s: string): string {
  try {
    return decodeURIComponent(s);
  } catch {
    return s;
  }
}

/**
 * Die drei Schritte in fester Reihenfolge. `mitEntropie: false` gilt für den
 * Wert eines Parameters: dort zählen nur Name und bekannte Muster (siehe
 * schwaerzeAdresse).
 */
function schwaerzeMit(text: string, mitEntropie: boolean): string {
  return text
    .replace(PEM_RE, GEHEIM)
    .replace(PRAEFIX_RE, (treffer: string, koerper: string) =>
      koerperZufaellig(koerper) ? GEHEIM : treffer,
    )
    .replace(ADRESSE_ODER_FOLGE_RE, (treffer: string, adresse: string | undefined) => {
      if (adresse !== undefined) return schwaerzeAdresse(adresse);
      return mitEntropie ? ersetzeFolge(treffer) : treffer;
    });
}

/** Einen Text schwärzen. Kürzer als der kürzeste Schlüssel: unverändert. */
export function schwaerzeText(text: string): string {
  return text.length < 19 ? text : schwaerzeMit(text, true);
}

/**
 * Einen Wert schwärzen — Texte darin, bis in jede Tiefe. Andere Werte und
 * Objekte, die keine einfachen Objekte sind (Datum, Puffer), bleiben, wie
 * sie sind. Das Original wird nie verändert.
 */
export function schwaerze<T>(wert: T): T {
  return tief(wert, new WeakMap()) as T;
}

function tief(w: unknown, gesehen: WeakMap<object, unknown>): unknown {
  if (typeof w === "string") return schwaerzeText(w);
  if (w === null || typeof w !== "object") return w;
  if (gesehen.has(w)) return gesehen.get(w);
  if (Array.isArray(w)) {
    const liste: unknown[] = [];
    gesehen.set(w, liste);
    for (const x of w) liste.push(tief(x, gesehen));
    return liste;
  }
  const art = Object.getPrototypeOf(w) as unknown;
  if (art !== Object.prototype && art !== null) return w;
  const neu: Record<string, unknown> = {};
  gesehen.set(w, neu);
  for (const [k, v] of Object.entries(w)) neu[k] = tief(v, gesehen);
  return neu;
}

/**
 * Die Eingabe eines Werkzeugaufrufs schwärzen: alles wie `schwaerze`, dazu
 * die Felder aus GEHEIME_EINGABEN ganz. Der Name darf mit dem Vorsatz der
 * Brücke kommen (`mcp__vespera__…`).
 */
export function schwaerzeEingabe(werkzeug: string | null | undefined, eingabe: unknown): unknown {
  const sicher = schwaerze(eingabe);
  const kern = (werkzeug ?? "").trim().replace(/^mcp__.+?__/, "");
  const felder = GEHEIME_EINGABEN[kern];
  if (!felder || sicher === null || typeof sicher !== "object" || Array.isArray(sicher)) return sicher;
  // Eine Abschrift — das Original gehört dem Aufrufer (und läuft ins Werkzeug).
  const o = { ...(sicher as Record<string, unknown>) };
  for (const f of felder) if (f in o) o[f] = GEHEIM;
  return o;
}
